import { en, awaitTopbar } from '@/globals/helpers';
import { PnLanguages } from '@/globals/types';
import { Component, h, Host, Element, Prop, forceUpdate, State, Watch } from '@stencil/core';
import { arrow_left } from 'pn-design-assets/pn-assets/icons.js';

const translations: Record<string, string> = {
  sv: 'Tillbaka',
  en: 'Back',
  da: 'Tilbage',
  fi: 'Takaisin',
  no: 'Tilbake',
};

/**
 * @slot breadcrumb - This slot is reserved for a `pn-text-link` or another complex breadcrumb links structure.
 * Do not use this slot and the `go-back-href` prop at the same time.
 * @slot buttons - This slot is used to align multiple `pn-button` elements. However, we recommend a single button.
 * @slot menu - This slot is reserved for a `pn-tablist`.
 * This slot allows you to use the `href` prop on `pn-tab`, turning them into links instead of buttons.
 */
@Component({
  tag: 'pn-header',
  styleUrl: 'pn-header.scss',
})
export class PnHeader {
  mo: MutationObserver;

  hasBreadcrumb: boolean = false;
  hasMenu: boolean = false;
  hasButtons: boolean = false;

  @Element() hostElement: HTMLElement;

  /** Heading text. */
  @Prop() heading: string;
  /** Set a inner container max-width, any width CSS value is accepted.  */
  @Prop() maxWidth?: string;
  @Watch('maxWidth')
  setMaxWidth() {
    if (!this.maxWidth) return;
    this.hostElement.style.setProperty('--max-width', this.maxWidth);
  }

  /** The href for the go back link. If this is provided a button be displayed. */
  @Prop() goBackHref?: string;
  /** The text for the back link. */
  @Prop() goBackText?: string;
  /** Set the language manually, only use this prop if the pnTopbar is not loaded. Supported: sv, en, da, fi and no. */
  @Prop() language?: PnLanguages = null;
  @Watch('language')
  setLanguage() {
    this.goBackTextTranslated = this.goBackText || translations[this.language || en];
  }

  @State() goBackTextTranslated: string;

  async componentWillLoad() {
    if (this.mo) this.mo.disconnect();

    this.mo = new MutationObserver(() => {
      forceUpdate(this.hostElement);
      this.checkSlots();
    });

    this.mo.observe(this.hostElement, { childList: true, subtree: true });

    this.checkSlots();
    this.setMaxWidth();
    this.setLanguage();

    if (this.language) return;
    await awaitTopbar(this.hostElement);
  }

  checkSlots() {
    this.hasBreadcrumb = !!this.hostElement.querySelector<HTMLDivElement>('[slot="breadcrumb"]');
    this.hasMenu = !!this.hostElement.querySelector<HTMLPnTablistElement>('[slot="menu"]');
    this.hasButtons = !!this.hostElement.querySelector<HTMLDivElement>('[slot="buttons"]');
  }

  showBreadcrumb() {
    return this.hasBreadcrumb || !!this.goBackHref?.length;
  }

  render() {
    return (
      <Host>
        <header class="pn-header">
          <div class="pn-header-container">
            <div class="pn-header-breadcrumbs" hidden={!this.showBreadcrumb()}>
              {this.goBackHref && (
                <pn-text-link label={this.goBackTextTranslated} href={this.goBackHref} icon={arrow_left} leftIcon />
              )}
              <slot name="breadcrumb"></slot>
            </div>

            <div class="pn-header-content">
              {this.heading && <h1>{this.heading}</h1>}
              <slot></slot>
            </div>
            <div class="pn-header-buttons" hidden={!this.hasButtons}>
              <slot name="buttons"></slot>
            </div>
            <slot name="menu"></slot>
          </div>
        </header>
      </Host>
    );
  }
}
